import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Paper,
  Stack,
} from '@mui/material';
import { getSubProductConditions } from 'lib/api';
import { checkIfBusinessIsBlacklisted } from 'lib/api/blackList';
import { getCompanyConfig } from 'lib/api/company';
import { getLastActiveQuote } from 'lib/api/factoring';
import CustomButton from 'lib/designSystem/Button';
import HeaderWithActions from 'lib/designSystem/HeaderWithActions';
import { getRealDaysToCount } from 'lib/functions/dates';
import { formatMoneyBack } from 'lib/functions/utils';
import ExportDialog from 'pages/Finance/Components/Financing/common/SimulationDoneLive/components/ExportDialog';
import PreviousOperationPanel from 'pages/Finance/Components/Financing/common/SimulationDoneLive/components/PreviousOperationPanel';
import SimulationDetailsPanel from 'pages/Finance/Components/Financing/common/SimulationDoneLive/components/SimulationDetailsPanel';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { initialFormData } from './components/SimulatorFormData';
import SimulatorParameters from './components/SimulatorParameters';
import {
  LastActiveQuote,
  SimulatorDocument,
  SimulatorFormData,
  SimulatorProps,
} from './types';

const SimulatorDialog: React.FC<SimulatorProps> = ({
  open,
  setOpen,
  business,
  subProduct,
}) => {
  const dispatch = useDispatch();

  const [formData, setFormData] = useState<SimulatorFormData>(initialFormData);

  const [expandedPanel, setExpandedPanel] = useState<string | false>('panel1');
  const [lastActiveQuote, setLastActiveQuote] =
    useState<LastActiveQuote | null>(null);
  const [loadingLastQuote, setLoadingLastQuote] = useState(false);
  const [isBlacklisted, setIsBlacklisted] = useState(false);
  const [documents, setDocuments] = useState<SimulatorDocument[]>([]);
  const [selected, setSelected] = useState<string[]>([]);
  const [documentsToApply, setDocumentsToApply] = useState<SimulatorDocument[]>(
    [],
  );
  const [subProductConditions, setSubProductConditions] = useState<{
    fundRate: number;
    spread: number;
    advance: number;
    arrearRate: number;
    operationCostFixed: number;
    operationCostVariable: number;
  } | null>(null);
  const [expensesHasIVA, setExpensesHasIVA] = useState(false);
  const [openExportDialog, setOpenExportDialog] = useState(false);
  const [simulatorDocuments, setSimulatorDocuments] = useState<
    Array<{
      id: string;
      amount: number;
      paymentDate: string;
      debtorRut: string;
      active: boolean;
    }>
  >([]);

  // Calcular monto total desde simulatorDocuments
  const operationAmount =
    simulatorDocuments.reduce(
      (sum, doc) => sum + (doc.active ? doc.amount : 0),
      0,
    ) || 0;
  const operationAdvanced = operationAmount * (Number(formData.advance) / 100);

  const maxAmount =
    operationAdvanced -
    (Number(formData.operationCostComissionVariable) || 0) -
    (Number(formData.operationCostComissionFixed) || 0) -
    (Number(formData.expensesCost) || 0);

  const fundRate = parseFloat(
    (subProductConditions?.fundRate || formData.fundRate || 0).toFixed(2),
  );
  const spread =
    subProductConditions?.spread !== undefined
      ? parseFloat(subProductConditions.spread.toFixed(2))
      : Number(formData.rate) - fundRate > 0
        ? parseFloat((Number(formData.rate) - fundRate).toFixed(2))
        : 0;

  let operationCostRate = 0;
  let operationCostFundRate = 0;
  let operationCostSpread = 0;

  const operationDate = new Date(formData.requestDate);

  // Construir documentos del selectedRequest desde simulatorDocuments
  const requestDocuments = simulatorDocuments.map((doc) => {
    const paymentDate = new Date(doc.paymentDate);
    const daysToCount = getRealDaysToCount(paymentDate, operationDate);
    const amountFinanced = Math.round(
      doc.amount * (Number(formData.advance) / 100),
    );
    const documentCostRate = daysToCount
      ? Math.round(
          (daysToCount / 30) * (Number(formData.rate) / 100) * amountFinanced,
        )
      : 0;

    return {
      folio: doc.id,
      amount: doc.amount,
      amountDocument: doc.amount,
      amountFinanced: amountFinanced,
      daysToCount: daysToCount,
      paymentDate: doc.paymentDate,
      active: doc.active,
      costRate: documentCostRate,
      stakeholderIdentifier: doc.debtorRut || '12345678-9',
      stakeholderName: 'Deudor Simulación',
      reclamo: false,
      notaCredito: false,
    };
  });

  const selectedRequest = {
    businessName: business?.businessName || 'Cliente Simulación',
    businessIdentifier: business?.identifier || '12345678-9',
    subProductName: 'Factura Electrónica',
    requestDate: new Date().toISOString(),
    operationDate: new Date().toISOString(),
    amount: operationAmount,
    executiveName: 'Usuario de Simulación',
    arrearRate: Number(formData.arrearRate) || 0,
    documents: requestDocuments,
    expensesIVA: expensesHasIVA,
  };

  // Calcular costos por documento
  for (const document of simulatorDocuments) {
    if (document.active) {
      const documentAmount = document.amount;
      const amountFinanced = Math.round(
        documentAmount * (Number(formData.advance) / 100),
      );
      const paymentDate = new Date(document.paymentDate);
      const daysToCount = getRealDaysToCount(paymentDate, operationDate);

      // Calcular costo total de la tasa
      const totalCostRate = daysToCount
        ? Math.round(
            (daysToCount / 30) * (Number(formData.rate) / 100) * amountFinanced,
          )
        : 0;

      // Calcular costo del fundRate
      const fundCostRate = daysToCount
        ? Math.round((daysToCount / 30) * (fundRate / 100) * amountFinanced)
        : 0;

      // Calcular costo del spread
      const spreadCostRate = daysToCount
        ? Math.round((daysToCount / 30) * (spread / 100) * amountFinanced)
        : 0;

      operationCostRate += totalCostRate;
      operationCostFundRate += fundCostRate;
      operationCostSpread += spreadCostRate;
    }
  }

  const operationIva =
    ((Number(formData.operationCostComissionFixed) || 0) +
      (Number(formData.operationCostComissionVariable) || 0)) *
    0.19;

  const expensesIva = expensesHasIVA
    ? (Number(formData.expensesCost) || 0) * 0.19
    : 0;

  const operationToDepost =
    operationAdvanced -
    operationCostRate -
    (Number(formData.operationCostComissionFixed) || 0) -
    (Number(formData.operationCostComissionVariable) || 0) -
    (Number(formData.expensesCost) || 0) -
    operationIva -
    expensesIva -
    (Number(formData.guaranteeAmount) || 0) -
    (Number(formData.amountToApply) || 0);

  const operationDetails = {
    operationAmount: operationAmount,
    operationAdvanced: operationAdvanced,
    operationCostRate: operationCostRate,
    operationCostComissionFixed:
      Number(formData.operationCostComissionFixed) || 0,
    operationCostComissionVariable:
      Number(formData.operationCostComissionVariable) || 0,
    operationToDepost: operationToDepost,
    operationIva: operationIva,
    expensesIva: expensesIva,
    operationAmountToApply: Number(formData.amountToApply) || 0,
    expensesCost: Number(formData.expensesCost) || 0,
    advance: Number(formData.advance) || 0,
    rate: Number(formData.rate) || 0,
    arrearRate: Number(formData.arrearRate) || 0,
    guaranteeAmount: Number(formData.guaranteeAmount) || 0,
    fundRate: fundRate,
    spread: spread,
    operationCostFundRate: operationCostFundRate,
    operationCostSpread: operationCostSpread,
    expensesDetails: [
      {
        name: 'Otros gastos',
        amount: Number(formData.expensesCost) || 0,
        hasIva: expensesHasIVA,
      },
    ],
  };

  const requestConfiguration = {
    factoringOperationsSystemFlag: true,
    expensesHasIVA: expensesHasIVA,
    fixedArrearRate: false,
  };

  const inRange = (value: number | string) => {
    return Number(value) >= 0 && Number(value) <= 100;
  };

  const handleChange = (id: string, newValue: string) => {
    const moneyIds = [
      'totalAmount',
      'operationCostComissionFixed',
      'expensesCost',
      'guaranteeAmount',
    ];
    const value = moneyIds.includes(id)
      ? formatMoneyBack(newValue) || 0
      : newValue;

    if (
      isNaN(Number(value)) &&
      id !== 'guaranteeObservation' &&
      id !== 'arrearType' &&
      id !== 'arrearTableId' &&
      id !== 'paymentDate' &&
      id !== 'requestDate'
    )
      return;
    if (id === 'advance' && !inRange(value)) return;
    if (id === 'rate' && !inRange(value)) return;
    if (id === 'arrearRate' && !inRange(value)) return;
    if (id === 'operationCostComissionVariable' && !inRange(value)) return;

    if (id === 'rate') {
      const numValue = Number(value);
      if (!isNaN(numValue)) {
        const currentFundRate = subProductConditions?.fundRate || fundRate;
        const newSpread = parseFloat((numValue - currentFundRate).toFixed(2));

        if (subProductConditions) {
          setSubProductConditions({
            ...subProductConditions,
            spread: newSpread >= 0 ? newSpread : 0,
          });
        }
      }

      setFormData({
        ...formData,
        rate: value,
      });
      return;
    }

    if (id === 'operationCostComissionVariable') {
      // Calcular monto total desde simulatorDocuments
      const totalAmount = simulatorDocuments.reduce(
        (sum, doc) => sum + (doc.active ? doc.amount : 0),
        0,
      );
      const variableAmount = totalAmount * (Number(value) / 100);
      setFormData({
        ...formData,
        operationCostComissionVariable: variableAmount,
        operationCostComissionVariablePercentage: value,
      });
      return;
    }

    setFormData({
      ...formData,
      [id]: value,
    });
  };

  const handleAccordionChange =
    (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpandedPanel(isExpanded ? panel : false);
    };

  const handleSetFormData = (newFormData: SimulatorFormData) => {
    if (newFormData.rate && subProductConditions?.fundRate) {
      const currentFundRate = subProductConditions.fundRate;
      const newSpread = parseFloat(
        (Number(newFormData.rate) - currentFundRate).toFixed(2),
      );

      setSubProductConditions({
        ...subProductConditions,
        spread: newSpread >= 0 ? newSpread : 0,
      });
    }

    setFormData(newFormData);
  };

  const checkBusinessBlacklist = async () => {
    if (!business?.identifier) return;

    try {
      const response = await checkIfBusinessIsBlacklisted(
        dispatch,
        business.identifier,
      );
      setIsBlacklisted(response.isBlacklisted);
    } catch (error) {
      console.error('Error checking blacklist:', error);
    }
  };

  const loadLastActiveQuote = async () => {
    if (!business?.identifier) return;

    try {
      setLoadingLastQuote(true);
      const response = await getLastActiveQuote(dispatch, business.identifier);
      setLastActiveQuote(response);
    } catch (error) {
      console.error('Error loading last active quote:', error);
    } finally {
      setLoadingLastQuote(false);
    }
  };

  const loadExpensesHasIVA = async () => {
    try {
      const response = await getCompanyConfig(
        dispatch,
        'system_accounting_expensesHasIva',
      );
      const expensesHasIVAValue = response === '1';
      setExpensesHasIVA(expensesHasIVAValue);
      return expensesHasIVAValue;
    } catch (error) {
      console.error('Error loading expensesHasIVA configuration:', error);
      return false;
    }
  };

  const loadSubProductConditions = async () => {
    if (!business?.identifier) return;

    try {
      const conditions = await getSubProductConditions(dispatch, {
        businessId: business.identifier,
        subProductId: 'electronic-invoice',
      });
      setSubProductConditions(conditions);

      const calculatedRate =
        (conditions.fundRate || 0) + (conditions.spread || 0);
      if (calculatedRate > 0) {
        setFormData((prev) => ({
          ...prev,
          rate: calculatedRate,
          advance: conditions.advance,
          arrearRate: conditions.arrearRate,
          operationCostComissionFixed: conditions.operationCostFixed,
          operationCostComissionVariablePercentage:
            conditions.operationCostVariable,
        }));
      }
    } catch (error) {
      console.error('Error loading subProduct conditions:', error);
    }
  };

  useEffect(() => {
    if (open && business?.identifier) {
      void checkBusinessBlacklist();
      void loadLastActiveQuote();
      void loadSubProductConditions();
      void loadExpensesHasIVA();
    }
  }, [open, business?.identifier, subProduct?.id]);

  const handleExportSimulation = () => {
    setOpenExportDialog(true);
  };

  return (
    <Dialog open={open} fullWidth maxWidth="xl">
      <DialogTitle>
        <HeaderWithActions title="Simulador de Factoring" type="dialogHeader" />
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={0}>
          <Grid item xs={6.5}>
            <SimulatorParameters
              formData={formData}
              handleChange={handleChange}
              selectedRequest={selectedRequest}
              documents={documents}
              setDocuments={setDocuments}
              selected={selected}
              setSelected={setSelected}
              setDocumentsToApply={setDocumentsToApply}
              documentsToApply={documentsToApply}
              maxAmount={maxAmount}
              simulatorDocuments={simulatorDocuments}
              setSimulatorDocuments={setSimulatorDocuments}
              business={business}
            />
          </Grid>
          <Grid item xs={5.5}>
            <div style={{ padding: '0px 12px' }}>
              <Stack spacing={2}>
                <Paper
                  elevation={0}
                  sx={{
                    boxShadow: 'none',
                    border: '1px solid #e0e0e0',
                    overflow: 'hidden',
                    transition: 'all 0.2s ease-in-out',
                    cursor: 'pointer',
                    '&:hover': {
                      elevation: 4,
                      boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.15)',
                      transform: 'translateY(-1px)',
                    },
                  }}
                >
                  <SimulationDetailsPanel
                    expanded={expandedPanel === 'panel1'}
                    onAccordionChange={handleAccordionChange}
                    operationDetails={operationDetails}
                    selectedRequest={selectedRequest}
                    requestConfiguration={requestConfiguration}
                    businessBlacklisted={isBlacklisted}
                    stakeholdersBlacklisted={[]}
                    onExportSimulation={handleExportSimulation}
                  />
                </Paper>
                <Paper
                  elevation={0}
                  sx={{
                    boxShadow: 'none',
                    border: '1px solid #e0e0e0',
                    overflow: 'hidden',
                    transition: 'all 0.2s ease-in-out',
                    cursor: lastActiveQuote ? 'pointer' : 'default',
                    opacity: lastActiveQuote ? 1 : 0.6,
                    '&:hover': lastActiveQuote
                      ? {
                          elevation: 4,
                          boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.15)',
                          transform: 'translateY(-1px)',
                        }
                      : {},
                  }}
                >
                  <PreviousOperationPanel
                    expanded={expandedPanel === 'panel2'}
                    onAccordionChange={handleAccordionChange}
                    lastActiveQuote={lastActiveQuote}
                    loadingLastQuote={loadingLastQuote}
                    setFormData={handleSetFormData}
                    formData={formData}
                    onEditing={true}
                  />
                </Paper>
              </Stack>
            </div>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <CustomButton
          variant="contained"
          color="secondary"
          onClick={() => setOpen(false)}
        >
          Cerrar
        </CustomButton>
      </DialogActions>

      {/* Export Dialog */}
      {openExportDialog && (
        <ExportDialog
          open={openExportDialog}
          setOpen={setOpenExportDialog}
          selectedRequest={selectedRequest}
          operationDetails={operationDetails}
          documentsToApply={documentsToApply}
          isSimulation={true}
        />
      )}
    </Dialog>
  );
};

export default SimulatorDialog;
